import type { ProjectDocument } from "@esi/shared";
import { t } from "../i18n/i18n.ts";
import type { AccessState } from "./AccessFields.tsx";
import {
  fileProblem,
  type Pending,
  resumable,
  UploadCancelled,
  UploadError,
  type UploadHooks,
  type UploadStep,
} from "./upload.ts";

/**
 * A batch of uploads in the browser (specs/project-files "Batch upload in the web app"): each
 * file still runs the per-file intent → PUT → confirm flow, a few at a time. Pure state and
 * logic, without the DOM.
 */

/** Files per batch: a dialog list without virtualization, well under any API throttling. */
export const MAX_BATCH_FILES = 20;
/** Files sent at once: enough to fill an office uplink without starving the app's requests. */
export const UPLOAD_CONCURRENCY = 3;

/**
 * `ready`: chosen, not started. `queued` waits for a slot; `uploading` covers the intent and the
 * PUT (`step` says which); `not_sent` was still queued when the batch stopped.
 */
export type BatchStatus =
  | "ready"
  | "queued"
  | "uploading"
  | "confirming"
  | "done"
  | "failed"
  | "cancelled"
  | "not_sent";

/** `invalid` and `duplicate` keep the file out of the batch; the name clashes only warn. */
export type BatchIssueKind = "invalid" | "duplicate" | "sameNameInProject" | "sameNameInBatch";

export interface BatchIssue {
  kind: BatchIssueKind;
  message: string;
}

export interface BatchItem {
  readonly id: string;
  readonly file: File;
  /** This file's own access; the batch's applies otherwise. */
  override?: AccessState | undefined;
  issues: readonly BatchIssue[];
  status: BatchStatus;
  step?: UploadStep | undefined;
  progress: { sent: number; total: number };
  /** Its document, declared but not confirmed: a retry resumes it rather than declaring again. */
  pending?: Pending | undefined;
  error?: string | undefined;
  /** A failure no retry can fix (the tenant's storage is full). */
  final?: boolean | undefined;
  document?: ProjectDocument | undefined;
}

const BLOCKING: ReadonlySet<BatchIssueKind> = new Set(["invalid", "duplicate"]);

/** Whether `item` is left out of the batch (unsupported, too large, or a copy of another). */
export const blocked = (item: Pick<BatchItem, "issues">) =>
  item.issues.some((i) => BLOCKING.has(i.kind));

export const IN_FLIGHT: ReadonlySet<BatchStatus> = new Set(["uploading", "confirming"]);

/** Started and not finished: waiting for a slot or on its way. */
export const unfinished = (item: Pick<BatchItem, "status">) =>
  item.status === "queued" || IN_FLIGHT.has(item.status);

/**
 * Whether `item`'s access can still change: before it starts, or after a failure that left no
 * document (a declared document keeps its metadata until it is abandoned).
 */
export const accessEditable = (item: Pick<BatchItem, "status" | "pending" | "final">) =>
  item.status === "ready" || (item.status === "failed" && !item.pending && !item.final);

/** The share of a file's bytes sent, in whole percent. */
export const percentOf = (progress: { sent: number; total: number }) =>
  progress.total > 0 ? Math.round((progress.sent / progress.total) * 100) : 0;

/** Rows that can leave the list: not on its way and without a document to finish. */
export const removable = (item: Pick<BatchItem, "status" | "pending">) =>
  !unfinished(item) && item.status !== "done" && !item.pending;

let nextId = 0;

/**
 * Appends `files` as `ready` items, each checked by `fileProblem`, up to `MAX_BATCH_FILES` in
 * all; `dropped` counts the files over the limit.
 */
export function addFiles(
  items: readonly BatchItem[],
  files: readonly File[],
  maxUploadMb: number,
): { items: BatchItem[]; dropped: number } {
  const room = Math.max(0, MAX_BATCH_FILES - items.length);
  const added = files.slice(0, room).map((file): BatchItem => {
    const problem = fileProblem(file, maxUploadMb);
    nextId += 1;
    return {
      id: `file-${nextId}`,
      file,
      issues: problem ? [{ kind: "invalid", message: problem }] : [],
      status: "ready",
      progress: { sent: 0, total: file.size },
    };
  });
  return { items: [...items, ...added], dropped: files.length - added.length };
}

/** SHA-256 of a file's content, hex. */
export async function sha256(file: Blob): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", await file.arrayBuffer());
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, "0")).join("");
}

const sameName = (name: string) => name.normalize("NFC").trim().toLowerCase();

/**
 * Recomputes the duplicate issues of every item, in order:
 * - the same content as an earlier file leaves the later one out. Only files that share a byte
 *   size with another are hashed, one at a time, so large batches are not read into memory;
 * - the same name as a project file the user can see (`visibleTitles`), or as an earlier file
 *   of the batch, warns.
 *
 * `hashOf` is memoized by the caller across calls.
 */
export async function findDuplicates(
  items: readonly BatchItem[],
  visibleTitles: readonly string[],
  hashOf: (file: File) => Promise<string> = sha256,
  /** True once a newer scan replaced this one: it stops reading files. */
  superseded: () => boolean = () => false,
): Promise<Map<string, BatchIssue[]>> {
  // Files that will not be sent (cancelled, not sent, refused for good) are no originals.
  const candidates = items.filter(
    (i) =>
      !i.issues.some((issue) => issue.kind === "invalid") &&
      i.status !== "cancelled" &&
      i.status !== "not_sent" &&
      !i.final,
  );
  const sizes = new Map<number, number>();
  for (const item of candidates) {
    sizes.set(item.file.size, (sizes.get(item.file.size) ?? 0) + 1);
  }
  const hashes = new Map<string, string>();
  for (const item of candidates) {
    if (superseded()) {
      break;
    }
    if ((sizes.get(item.file.size) ?? 0) > 1) {
      try {
        // One at a time: at most one file's bytes in memory.
        hashes.set(item.id, await hashOf(item.file));
      } catch {
        // Unreadable now (moved, changed): no content match; its upload reports the problem.
      }
    }
  }
  const project = new Set(visibleTitles.map(sameName));
  const firstByHash = new Map<string, BatchItem>();
  const seenNames = new Set<string>();
  const out = new Map<string, BatchIssue[]>();
  const counted = new Set(candidates.map((i) => i.id));
  for (const item of items) {
    const issues = item.issues.filter((i) => i.kind === "invalid");
    out.set(item.id, issues);
    if (issues.length > 0 || !counted.has(item.id)) {
      continue;
    }
    const hash = hashes.get(item.id);
    const original = hash ? firstByHash.get(hash) : undefined;
    if (original) {
      issues.push({
        kind: "duplicate",
        message: t("upload.batch.duplicateOf", { name: original.file.name }),
      });
      continue;
    }
    if (hash) {
      firstByHash.set(hash, item);
    }
    const name = sameName(item.file.name);
    if (project.has(name)) {
      issues.push({ kind: "sameNameInProject", message: t("upload.batch.sameNameInProject") });
    } else if (seenNames.has(name)) {
      issues.push({ kind: "sameNameInBatch", message: t("upload.batch.sameNameInBatch") });
    }
    seenNames.add(name);
  }
  return out;
}

/** The per-file flow, as the runner drives it. */
export interface BatchUploads {
  /** Declares the file and sends it: a new document. */
  start: (item: BatchItem, hooks: UploadHooks) => Promise<ProjectDocument>;
  /** Sends the file of a declared document again, or only confirms it once stored. */
  resume: (item: BatchItem, pending: Pending, hooks: UploadHooks) => Promise<ProjectDocument>;
  /** Removes a declared document that will not be finished (best effort). */
  abandon: (pending: Pending) => Promise<void>;
  /** The document once confirmed, or null: decides a cancel that raced the confirmation. */
  confirmed: (pending: Pending) => Promise<ProjectDocument | null>;
}

export interface BatchRunner {
  items(): readonly BatchItem[];
  /** Replaces the items not started yet (adding, removing, overriding access). */
  edit(change: (items: readonly BatchItem[]) => readonly BatchItem[]): void;
  /** Applies duplicate issues computed for items still present. */
  setIssues(issues: ReadonlyMap<string, BatchIssue[]>): void;
  /** Queues every ready item `sendable` accepts. */
  start(sendable: (item: BatchItem) => boolean): void;
  cancel(id: string): void;
  /** Cancels every unfinished item and removes the documents of failed ones. */
  cancelAll(): void;
  retry(id: string): void;
  /** Starts no further item: queued ones become `not_sent` with `reason`. */
  stop(reason: string): void;
}

/**
 * Runs a batch: at most `concurrency` items in flight, the next one starting when a slot frees.
 *
 * Cancelling stops a queued item at once and aborts a PUT. A request to the API (the intent or
 * the confirmation) is never cut off: the cancel applies at the next step, so the document it
 * declared is known and removed, and a confirmation that got through keeps its file. A final
 * storage-limit failure stops the batch.
 */
export function createBatchRunner(options: {
  uploads: BatchUploads;
  onChange: (items: readonly BatchItem[]) => void;
  /**
   * Nothing is in flight any more and the documents it touched are settled (uploaded, removed):
   * time to refresh the file list. Called even when nobody shows the batch any more.
   */
  onIdle?: () => void;
  concurrency?: number;
}): BatchRunner {
  const { uploads, onChange } = options;
  const concurrency = options.concurrency ?? UPLOAD_CONCURRENCY;
  let items: readonly BatchItem[] = [];
  let stopped = false;
  const controllers = new Map<string, AbortController>();
  /** Items to cancel once their API request answers. */
  const cancelling = new Set<string>();

  const commit = (next: readonly BatchItem[]) => {
    items = next;
    onChange(items);
  };
  const find = (id: string) => items.find((i) => i.id === id);
  const update = (id: string, patch: Partial<BatchItem>) =>
    commit(items.map((i) => (i.id === id ? { ...i, ...patch } : i)));
  const idle = () => {
    if (controllers.size === 0) {
      options.onIdle?.();
    }
  };
  const abandon = (pending: Pending) => void uploads.abandon(pending).finally(idle);

  function pump() {
    while (!stopped && controllers.size < concurrency) {
      const next = items.find((i) => i.status === "queued");
      if (!next) {
        return;
      }
      void run(next);
    }
  }

  async function run(item: BatchItem) {
    const controller = new AbortController();
    if (item.pending && !resumable(item.pending)) {
      // The link expired while it waited. Declaring the file again would take the batch's
      // access as it is now: the person checks the file's access and retries instead.
      expire(item.id, item.pending);
      return;
    }
    controllers.set(item.id, controller);
    const pending = item.pending;
    update(item.id, {
      pending: undefined,
      status: "uploading",
      step: pending ? "put" : "intent",
      error: undefined,
      progress: { sent: 0, total: item.file.size },
    });
    const hooks: UploadHooks = {
      signal: controller.signal,
      onStep: (step) => {
        update(item.id, { status: step === "confirm" ? "confirming" : "uploading", step });
        if (step !== "intent" && cancelling.has(item.id)) {
          // The intent answered: cancel before the next request, with the document known.
          controller.abort();
        }
      },
      // Committed when the shown percentage changes, not on every progress event.
      onProgress: (sent, total) => {
        const before = find(item.id)?.progress;
        if (!before || percentOf(before) !== percentOf({ sent, total }) || sent === total) {
          update(item.id, { progress: { sent, total } });
        }
      },
    };
    try {
      const document = await (pending
        ? uploads.resume(item, pending, hooks)
        : uploads.start(item, hooks));
      // The server is the authority: a confirmed file stays, even when its cancel came late.
      update(item.id, { status: "done", document, pending: undefined, error: undefined });
    } catch (error) {
      await settleFailure(item.id, error);
    } finally {
      controllers.delete(item.id);
      cancelling.delete(item.id);
      pump();
      idle();
    }
  }

  async function settleFailure(id: string, error: unknown) {
    if (error instanceof UploadCancelled) {
      await settleCancelled(id, error.pending);
      return;
    }
    // Storage full ends the batch, whether or not a cancel was asked meanwhile.
    if (error instanceof UploadError && error.final) {
      update(id, { status: "failed", error: error.message, final: true, pending: undefined });
      if (error.final.leftover) {
        abandon(error.final.leftover);
      }
      stop(error.message);
      return;
    }
    if (cancelling.has(id)) {
      // Asked to cancel, then failed anyway: cancelled, and nothing left behind. A lost answer
      // to the confirmation may hide a success, so a stored file is checked first.
      await settleCancelled(id, error instanceof UploadError ? error.resume : undefined);
      return;
    }
    if (error instanceof UploadError) {
      update(id, { status: "failed", error: error.message, pending: error.resume });
      return;
    }
    update(id, { status: "failed", error: t("errors.unexpected"), pending: undefined });
  }

  async function settleCancelled(
    id: string,
    pending: Pending | undefined,
    status: "cancelled" | "not_sent" = "cancelled",
  ) {
    if (pending?.stored) {
      // Stored before the abort: its confirmation may have gone through.
      const confirmed = await uploads.confirmed(pending);
      if (confirmed) {
        update(id, { status: "done", document: confirmed, pending: undefined });
        return;
      }
    }
    if (pending) {
      await uploads.abandon(pending);
    }
    update(id, { status, pending: undefined });
  }

  /** Removes an expired document; the file fails with its access editable again. */
  function expire(id: string, pending: Pending) {
    abandon(pending);
    update(id, { status: "failed", pending: undefined, error: t("upload.expired") });
  }

  function cancel(id: string) {
    const item = find(id);
    if (!item) {
      return;
    }
    if ((item.status === "queued" || item.status === "ready") && !item.pending) {
      update(id, { status: "cancelled" });
      return;
    }
    if (item.pending && (item.status === "queued" || item.status === "failed")) {
      // A retry waiting for a slot, or a failure, still holds a declared document. Cancelled at
      // once (nothing starts or retries it), then settled: kept if its confirmation got through.
      update(id, { status: "cancelled", pending: undefined });
      void settleCancelled(id, item.pending).finally(idle);
      return;
    }
    if (!IN_FLIGHT.has(item.status)) {
      return;
    }
    if (item.step === "put") {
      controllers.get(id)?.abort();
    } else {
      cancelling.add(id);
    }
  }

  function stop(reason: string) {
    stopped = true;
    const held = items.filter((i) => i.status === "queued" && i.pending);
    commit(
      items.map((i) => (i.status === "queued" ? { ...i, status: "not_sent", error: reason } : i)),
    );
    // Retries that waited still hold a declared document: removed (or kept once confirmed).
    for (const item of held) {
      void settleCancelled(item.id, item.pending, "not_sent").finally(idle);
    }
  }

  return {
    items: () => items,
    edit: (change) => commit(change(items)),
    setIssues: (issues) =>
      commit(
        items.map((i) => {
          const next = issues.get(i.id);
          return next && i.status === "ready" ? { ...i, issues: next } : i;
        }),
      ),
    start(sendable) {
      stopped = false;
      commit(
        items.map((i) =>
          i.status === "ready" && !blocked(i) && sendable(i) ? { ...i, status: "queued" } : i,
        ),
      );
      pump();
    },
    cancel,
    cancelAll() {
      for (const item of items) {
        if (unfinished(item) || (item.status === "failed" && item.pending)) {
          cancel(item.id);
        }
      }
    },
    retry(id) {
      const item = find(id);
      if (item?.status !== "failed" || item.final) {
        return;
      }
      if (item.pending && !resumable(item.pending)) {
        expire(id, item.pending);
        return;
      }
      stopped = false;
      update(id, { status: "queued", error: undefined });
      pump();
    },
    stop,
  };
}
